/**
 * 주간 주요재고 「하나씩 확인」 API 클라이언트 (v2.198.0 → 2차 v2.202.0) — backend /stock/check/* 와 1:1.
 *
 * ⚠**서버 타입을 import 하지 않는다**(프론트는 늘 그랬다). 그래서 서버에서 값 이름을 바꾸면
 *   typecheck 가 못 잡는다 — 이름을 고칠 때는 이 파일을 함께 볼 것(2026-09-11 에 데였다).
 * ⭐조치 내용 목록·다름 메모 칩은 **서버가 내려 준다**(`reasons`·`diffChips`) — 화면에 따로 적지 않는다.
 * ⭐현황표 문턱(`gate`)·단계(`stage`)도 **서버가 판정**한다 — 화면은 계산하지 않고 그대로 그린다.
 */

import type { ApiResponse } from './types';
import type { LocQty, StockLocation } from './stock-locations';

const BASE = '/api';

/** 맞음 / 조치 필요 / 조치 완료 */
export type CheckStatus = 'ok' | 'diff' | 'resolved';

/** 409·400 을 가려 화면이 다시 불러오게 — status·code 를 들고 던진다 */
export class CheckApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function send<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
  let json: ApiResponse<T> | null = null;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new CheckApiError(res.status, 'BAD_RESPONSE', `서버 응답을 읽지 못했습니다 (${res.status})`);
  }
  if (!json.ok) {
    // ⚠우리 모양이 아닌 오류(서버 앞단 404 등)는 error 가 객체가 아니다 — 글자 없는 띠 대신 상태 번호라도(v2.225.1)
    const e = (json as unknown as { error?: unknown }).error;
    const code = e && typeof e === 'object' ? String((e as { code?: unknown }).code ?? 'ERROR') : 'ERROR';
    const em = e && typeof e === 'object' ? (e as { message?: unknown }).message : undefined;
    const msg = typeof em === 'string' && em ? em : `서버가 요청을 처리하지 못했습니다 (${res.status})`;
    throw new CheckApiError(res.status, code, msg);
  }
  return json.data;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return send<T>(path, {
    method: 'POST',
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
}

/* ── 이번 주 목록 ─────────────────────────────────── */

export interface ResolvedMark {
  by: string;
  byName: string | null;
  at: string;
  /** 다시 센 수량 (= 그 순간 장부) */
  qty: number;
  systemQty: number;
  reason: string;
  reasonLabel: string;
  note: string | null;
  /** 장부 숫자가 그때와 달라졌나 (기록을 고쳐 맞춘 것) */
  ledgerChanged: boolean;
}

export interface StockCheckMark {
  status: CheckStatus;
  /** 누를 때 화면에 떠 있던 현재고 (옛 화면이면 null) */
  seenQty: number | null;
  /** 저장 순간 서버가 다시 읽은 현재고 */
  systemQty: number | null;
  /** 기준 수량 = seenQty ?? systemQty — 「그때 시스템」으로 보여 줄 숫자 */
  baselineQty: number | null;
  actualQty: number | null;
  note: string | null;
  /** 발견자(처음 적은 사람) */
  checkedBy: string | null;
  checkedByName: string | null;
  checkedAt: string | null;
  /** 장소별 센 수(v2.226.0) — 옛 기록·옛 서버는 null/없음 */
  locQty?: LocQty | null;
  /** 2차 이전(또는 옛 코드)이 쓴 줄 — 과정 이력이 없다 */
  legacy: boolean;
  /** status=resolved 일 때만 */
  resolved: ResolvedMark | null;
}

export interface PrevDiffMark {
  /** 지난주 월요일 */
  weekDate: string;
  baselineQty: number | null;
  actualQty: number | null;
  note: string | null;
}

export interface StockCheckItem {
  itemCode: string;
  itemName: string | null;
  itemGroup: string | null;
  displayGroup: string | null;
  currentStock: number;
  pendingDelivery: number;
  incoming: number;
  leased: number;
  availableStock: number;
  safeStock: number;
  /** 가용재고가 0 이하이거나 안전재고에 못 미친다 (판정은 서버 lib/stock/check.ts 한 곳) */
  short: boolean;
  /** 이번 회차에 확인했으면 그 기록, 아니면 null */
  check: StockCheckMark | null;
  /** 지난주에 「조치 필요」로 끝난 품목이면 그 기록 (딱지로 보여 준다) */
  prevDiff: PrevDiffMark | null;
}

export interface StatusSendMark {
  sentAt: string;
  /** 표에 담긴 품목 수 — **주요재고 전체**라 확인 목록(HDD·SSD)보다 많다 */
  itemCount: number | null;
  sentByName: string;
  /** sending·unknown = 나갔는지 확실하지 않음 · failed = 안 나감 */
  result: 'sending' | 'sent' | 'failed' | 'unknown';
  round: number;
}

export type GateBlock = 'unchecked' | 'open_diff' | null;
export interface StatusGate {
  /** 📊 현황표를 보낼 수 있나 */
  pass: boolean;
  block: GateBlock;
  /** 막혔을 때 보일 말 */
  message: string | null;
  total: number;
  checked: number;
  /** 맞음 */
  matched: number;
  /** 조치 필요 */
  open: number;
  /** 조치 완료 */
  resolved: number;
}

/** checking = 아직 세는 중 · fixing = 조치 필요가 남음 · ready = 표를 보낼 차례 · sent = 이번 회차 표를 보냄 */
export type WeekStage = 'checking' | 'fixing' | 'ready' | 'sent';

/**
 * ✅ 완료 보고가 방에 나갔나 — **서버가 판정해 내려 준다** (v2.202.2 · backend lib/stock/check.ts noticeState)
 *   none 아직 · sending 보내는 중 · sent 보냄 · failed 확실히 못 보냄 · uncertain **나갔는지 모름**
 */
export type NoticeState = 'none' | 'sending' | 'sent' | 'failed' | 'uncertain';

/** 서버가 판정을 안 준 옛 응답(배포 도중)이면 종전 셈법 — 보낸 시각이 있으면 보냄, 사유만 있으면 못 보냄 */
export function noticeStateOf(x: {
  doneNoticeState?: NoticeState;
  doneNoticeAt: string | null;
  doneNoticeError?: string | null;
}): NoticeState {
  if (x.doneNoticeState) return x.doneNoticeState;
  if (x.doneNoticeAt) return 'sent';
  return x.doneNoticeError ? 'failed' : 'none';
}

export interface ReasonOption {
  key: string;
  label: string;
  /** 장부를 고쳐야 성립하는 조치(회수·입고·출고) — 장부가 그대로면 서버가 거절한다 */
  needsLedgerChange: boolean;
}

export interface StockCheckWeek {
  /** 그 주 월요일 (서버가 정한다) */
  weekDate: string;
  today: string;
  /** 지금 회차 (처음부터 다시 할 때마다 +1) */
  round: number;
  /** 조회전용(viewer)이면 false — 화면이 단추를 감춘다. 서버도 따로 막는다 */
  canEdit: boolean;
  /** 장소 칸(v2.226.0) — 옛 서버는 안 준다 */
  locations?: StockLocation[];
  /** [처음부터 다시 검수하기] — 관리(mgmt) 이상 */
  canRestart: boolean;
  /** ✅ 이번 회차 완료 보고가 방에 나간 시각 (없으면 null) */
  doneNoticeAt: string | null;
  /** ⚠ 완료 보고를 못 보낸 사유 (없으면 null) — 있으면 화면이 [다시 보내기]를 띄운다 */
  doneNoticeError: string | null;
  /** ✅ 완료 보고 상태 (v2.202.2 · 옛 서버는 안 준다 → noticeStateOf 로 읽을 것) */
  doneNoticeState?: NoticeState;
  /** 📊 이번 주 현황표를 마지막으로 누른 기록 (결과 무관 · 없으면 null) */
  lastStatusSend: StatusSendMark | null;
  gate: StatusGate;
  stage: WeekStage;
  /** [통과] 때 고르는 조치 내용 (서버가 정한 목록) */
  reasons: ReasonOption[];
  /** [다름] 메모에 덧붙이는 짧은 말 */
  diffChips: string[];
  /** 확인 담당 — 없으면 null */
  owner: { userId: string; label: string } | null;
  /** 이번 회차에 「다름」으로 적었다가 되돌린 수 */
  cancelledDiffs: number;
  items: StockCheckItem[];
  /** ⚠diff = **조치 필요(미해결)만** */
  summary: { total: number; checked: number; diff: number; resolved: number; short: number };
}

/** 이번 주 확인 목록 (week 를 주면 그 주) */
export function getWeek(week?: string): Promise<StockCheckWeek> {
  const qs = week ? `?week=${encodeURIComponent(week)}` : '';
  return send<StockCheckWeek>(`/stock/check${qs}`);
}

/* ── 기록 ─────────────────────────────────────────── */

export interface MarkInput {
  itemCode: string;
  status: 'ok' | 'diff';
  actualQty?: number | null;
  note?: string | null;
  /** 누를 때 화면에 떠 있던 현재고 — 「사람이 본 숫자」 */
  seenQty?: number | null;
  /** 장소별 센 수(v2.226.0) — 보내면 서버가 합계로 확인/다름을 판정(실제 수량 = 합계) */
  locQty?: LocQty;
}

export interface MarkResult {
  weekDate: string;
  round: number;
  checked: number;
  total: number;
  /** 이 누름으로 「다 확인했다」 보고가 방에 나갔나 */
  notified: boolean;
  eventId: number;
  /** 화면 숫자와 저장 순간 숫자가 달랐으면 둘 다 (막지 않고 알려만 준다) */
  moved: { seen: number; now: number } | null;
  /** 실제로 저장된 상태(장소별 수량이면 서버 판정 · 옛 서버는 없음) */
  status?: 'ok' | 'diff';
}

/** [확인]/[다름] — 조치 필요 → 맞음, 조치 완료 줄 덮어쓰기는 409 */
export function mark(input: MarkInput): Promise<MarkResult> {
  return post<MarkResult>('/stock/check', input);
}

export interface ResolveInput {
  itemCode: string;
  /** 화면에 떠 있던 「지금 시스템」 숫자 — 그 사이 바뀌었으면 409 */
  seenQty: number;
  /** 다시 센 수 — 없으면 seenQty 와 같다고 본다(한 번 누름) */
  recountQty?: number | null;
  /** 조치 내용 키 (reasons 의 key) */
  reason: string;
  note?: string | null;
}

/** 🔧 다시 세고 통과 — 다시 센 수 = 지금 장부 일 때만 (아니면 400) */
export function resolve(input: ResolveInput): Promise<{ eventId: number; itemCode: string }> {
  return post('/stock/check/resolve', input);
}

/** 조치 완료 취소 → 조치 필요로 */
export function unresolve(itemCode: string): Promise<{ eventId: number }> {
  return post('/stock/check/unresolve', { itemCode });
}

/** 확인 되돌리기 — 그 회차 완료 보고가 나간 뒤에는 409 */
export function unmark(itemCode: string): Promise<{ weekDate: string; itemCode: string; eventId: number }> {
  // ⭐품목코드는 본문으로(v2.225.1) — 경로에 넣으면 운영 nginx 가 빗금(EDS 16TB · Seagate 20TB)을 풀어 404
  return post('/stock/check/unmark', { itemCode });
}

/** 🔄 처음부터 다시 검수하기 (mgmt 이상) — expectRound 가 지금 회차와 다르면 409 */
export function restart(expectRound: number, reason?: string): Promise<{
  eventId: number; newRound: number; cleared: { total: number; matched: number; open: number; resolved: number };
}> {
  return post('/stock/check/restart', { expectRound, ...(reason ? { reason } : {}) });
}

/* ── 방으로 나가는 것 ─────────────────────────────── */

export interface SendTestResult {
  asOf: string;
  itemCount: number;
  shortCount: number;
  /** 실제로 보낸 글 — 화면에 그대로 보여 준다(무엇이 갔는지 눈으로 확인) */
  text: string;
}

/** 🔔 시험 발송 — 월요일에 나갈 글을 지금 메시지방으로 한 통 (admin 전용) */
export function sendTest(): Promise<SendTestResult> {
  return post<SendTestResult>('/stock/check/send-test');
}

/**
 * 📊 재고현황 표(그림)를 메시지방으로 — 예전 월요일 안내와 같은 형태.
 * ⚠담기는 것은 **주요재고 전체**라 확인 목록(HDD·SSD)보다 많다.
 * 「다 확인 + 조치 필요 0」일 때만 열린다(서버가 막는다).
 */
export function sendStatus(): Promise<{ asOf: string; itemCount: number; footer: string[] }> {
  return post('/stock/check/send-status');
}

/** 보내기 전 표 그림 주소 — `t` 는 **미리보기를 연 순간 한 번만** 정할 것(렌더마다 바꾸면 매번 새로 그린다) */
export function statusPreviewUrl(t: number): string {
  return `${BASE}/stock/check/status-preview?t=${t}`;
}

/**
 * ✅ 완료 보고 다시 보내기 — 웍스가 막혀 못 나갔을 때.
 * ⭐force — 「나갔는지 모름」인 보고를 **사람이 메시지방에 없는 것을 확인하고** 다시 보낼 때만 (v2.202.2).
 *   그냥 보내면 서버가 409 로 막는다(웍스가 이미 올려 둔 글 위에 한 통이 더 나갈 수 있다).
 */
export async function retryNotice(round?: number, force = false): Promise<void> {
  await post<unknown>('/stock/check/retry-notice', {
    ...(round === undefined ? {} : { round }),
    ...(force ? { force: true } : {}),
  });
}

/* ── ⑧ 지난 주 이력 (데스크톱) ────────────────────── */

export interface HistoryWeekRow {
  weekDate: string;
  /** 링크가 실제로 나가는 날(공휴일이면 미룬 날) */
  sendDay: string;
  /**
   * 그 주 월요일 글. kind — image: **옛 표 그림**(확인 링크가 생기기 전 · 2026-09-21 주) · link: 확인 링크
   * (v2.202.2 · 옛 서버는 kind 를 안 준다 → 링크로 본다)
   */
  link: { ok: boolean; sentAt: string | null; attempts: number; error: string | null; kind?: 'image' | 'link' } | null;
  scopeFixed: boolean;
  total: number | null;
  round: number;
  checked: number;
  matched: number;
  open: number;
  resolved: number;
  /** 되돌림 사건 수 */
  cancelled: number;
  /** 처음부터 다시 한 횟수 */
  restarts: number;
  checkers: string[];
  firstAt: string | null;
  lastAt: string | null;
  doneNoticeAt: string | null;
  doneNoticeError: string | null;
  /** v2.202.2 — noticeStateOf 로 읽을 것 */
  doneNoticeState?: NoticeState;
  statusSends: { count: number; lastAt: string | null; lastByName: string | null; uncertain: number };
  remind: { outcome: string; sentAt: string | null; error: string | null } | null;
  /** 2차 이전 기록 — 과정 이력 없음 */
  legacy: boolean;
  /** 사람이 적은 비고 (기계 메모는 서버가 걸러 낸다) */
  note: string | null;
}

export function getHistoryWeeks(limit = 12, before?: string): Promise<{ weeks: HistoryWeekRow[]; hasMore: boolean }> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (before) qs.set('before', before);
  return send(`/stock/check/weeks?${qs.toString()}`);
}

export interface HistoryItem {
  itemCode: string;
  itemName: string | null;
  status: CheckStatus | null;
  seenQty: number | null;
  systemQty: number | null;
  baselineQty: number | null;
  actualQty: number | null;
  /** 장소별 센 수(v2.226.0) */
  locQty?: LocQty | null;
  note: string | null;
  checkedByName: string | null;
  checkedAt: string | null;
  resolved: ResolvedMark | null;
  legacy: boolean;
}

export interface HistoryEvent {
  id: number;
  at: string;
  round: number;
  itemCode: string | null;
  itemName: string | null;
  action: string;
  /** 사람 말 (예: 다름 발견 · 조치 완료(통과) · 되돌림 · 처음부터 다시) */
  label: string;
  statusBefore: string | null;
  statusAfter: string | null;
  seenQty: number | null;
  systemQty: number | null;
  countedQty: number | null;
  note: string | null;
  reasonLabel: string | null;
  actorName: string;
  /** 그 회차 완료 보고가 나간 뒤에 일어난 일 */
  afterReport: boolean;
}

export interface HistoryRoundRow {
  itemCode: string;
  itemName: string | null;
  status: CheckStatus;
  baselineQty: number | null;
  actualQty: number | null;
  note: string | null;
  checkedByName: string | null;
  resolvedQty: number | null;
  reasonLabel: string | null;
}

export interface HistoryRound {
  round: number;
  closedAt: string;
  closedByName: string;
  doneNoticeAt: string | null;
  reason: string | null;
  rows: HistoryRoundRow[];
}

export interface HistoryStatusSend {
  sentAt: string;
  sentByName: string;
  result: string;
  error: string | null;
  footer: string | null;
  round: number;
  via: string;
}

export interface HistoryDetail {
  weekDate: string;
  round: number;
  scope: string[] | null;
  items: HistoryItem[];
  events: HistoryEvent[];
  /** 지난 회차들 ([처음부터 다시] 때 닫힌 것) */
  rounds: HistoryRound[];
  statusSends: HistoryStatusSend[];
  link: HistoryWeekRow['link'];
  doneNoticeAt: string | null;
  doneNoticeError: string | null;
  /** v2.202.2 — noticeStateOf 로 읽을 것 */
  doneNoticeState?: NoticeState;
  remind: HistoryWeekRow['remind'];
  note: string | null;
}

export function getHistory(week: string): Promise<HistoryDetail> {
  return send(`/stock/check/history?week=${encodeURIComponent(week)}`);
}
